/* eslint-disable camelcase, no-console */
import {
	type UserProfile as Profile,
	User,
	UserManager,
	type UserManagerSettings,
	WebStorageStateStore,
	InMemoryWebStorage,
} from "oidc-client-ts";
import join from "lodash/fp/join";

import { mapUserProfile } from "./userProfile";
import { config } from "../../config";
import type { OAuthConfig } from "..";
import type { AccessToken } from "../tokenHandling/tokenSlice";
import type { UserProfile } from "./loginSlice";

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const retrySigninSilent = (oauthConfig: OAuthConfig, userManager: UserManager) => {
	userManager.signinSilent().catch((error: Error) => {
		if (error.message === "login_required") {
			oauthConfig.onSessionExpired();
		} else {
			setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
		}
	});
};

export type SessionRenewedResult = {
	accessToken: AccessToken;
	idToken: Profile;
	profile: UserProfile;
	locale: string;
};

export const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
	accessToken: result.access_token,
	idToken: result.profile,
	locale: result.profile?.locale ?? "en-GB",
	profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
	const redirectUri = config.login.redirectUri;
	const silentRedirectUri = config.login.silentRedirectUri;

	const settings: UserManagerSettings = {
		authority: `${config.login.authority}`,
		client_id: `${config.login.clientId}`,
		loadUserInfo: false,
		redirect_uri: `${redirectUri}`,
		response_type: "code",
		scope: join(" ", config.login.oauthScope),
		silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
		includeIdTokenInSilentRenew: false,
		automaticSilentRenew: true,
		monitorSession: true,
		staleStateAgeInSeconds: 600,
		userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
		filterProtocolClaims: false,
	};

	return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
	userManager.events.addUserLoaded((user) => {
		oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
	});

	userManager.events.addUserUnloaded(() => {
		oauthConfig.onSessionExpired();
	});

	userManager.events.addSilentRenewError(() => {
		retrySigninSilent(oauthConfig, userManager);
	});

	userManager.events.addUserSignedOut(() => {
		oauthConfig.onSessionExpired();
	});

	return userManager;
};

export const configureMockUserManager = (oauthConfig: OAuthConfig): UserManager => {
	const signinSilent = () => {
		const userSettings = {
			// manual token
			access_token:
				"eyJraWQiOiJjMDNmNDMwMy05OTk4LTRkYmItYjgxNC03OGI0MmIwNmE0NDkiLCJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJzdWIiOiJwcm9kLXJpby11c2Vyczo4MmYyYmY2ZC01OWQzLTRiZmUtOTI3Mi1jMGRkYmUxMmQwYmEiLCJhY3IiOiJ1cm46cmlvOmxvYToxIiwiYXpwIjoiYTZhMTI5ZDItNmE3Ny00MjA2LTlhNTUtMGIzN2JjYjA0MjA5Iiwic2NvcGUiOiJhc3NldC1hZG1pbmlzdHJhdGlvbi5yZWFkIHBob25lIG9wZW5pZCBwcm9maWxlIHRhZ3MucmVhZCBlbWFpbCIsImlzcyI6Imh0dHBzOi8vYXV0aC5pYW0ucmlvLmNsb3VkIiwiZXhwIjoxNzczNjkyMTY2LCJpYXQiOjE3NzM2ODg1NjYsImp0aSI6IjVjMjgwN2ZlLTczYTEtNDE2OS1hM2JhLWEwZjYyZmJjYjY0NCIsImNsaWVudF9pZCI6ImE2YTEyOWQyLTZhNzctNDIwNi05YTU1LTBiMzdiY2IwNDIwOSIsImFjY291bnQiOiI0OTI0NTY4ZS0yM2YzLTQwZjktOTczMC1mZjg4ZWZjNDExNzIiLCJ0ZW5hbnQiOiJyaW8tYnJhemlsLnByb2QifQ.LmWHIJeIfdQpNoYk9Aa5bv2cl_Gzdao8GAwuy0Wfg24UrXMNjSkC0_tw9BBqX4P4J5ZmQ-UwpP666l3HodrcNHM43mdMntdJj-UyMJ810ZbcYaU3cdd8uOd2yDjuRAQvLnROj2o-rpR7CUEA-b-PkYb5ZaeSdNDltzcUAYiE5CQTBoMZpyasYngcGRO58dUteot7_3XB_7dmD1L7L2Jn75ZJxIR5hyeYArHfbrg5nfMl93ar46pbvZ1tbYvNI2o_8bu_rYFcBf59QTK3t0B3riCSmoGVm_GrQUHcBQIpnmqQivqIP_SiUdiLBpYZTXowXNbKjVgXheAUylNOJkFT_8SKZKnoYKCCyo-9dqMTmHxTPF3wiE0Y1GgXKhJI9CNv7ykekc-oJYEclGjvZVqKyKGdXSyPt-qnasLqefB6FljvAr0lAQkQGerKKMiwwlmAJG0CfA9A4FWJqlc_LTE1gN5eWczvnTVavvSrMCDV_i9gkGbaVwmHcypUMAZ1inno5vgfNgqzhYtA_iG28dFTvmejXuQf3lgKk98LnrWkec1lmKQhMTcr0IB0sUC6hK9VOiHxS-ED6Ix1Ilbvbbhsp8QZuOqxqD2oGU3YnX01KoYoj5pwOPopUBvZmxlVHEAR-WLOiPLCCY9mCVv6Gt5fYNVtsSDRxkx5BWQ9cWCtCUw",
			profile: {
				iss: "Issuer Identifier",
				aud: "Audience(s): client_id",
				exp: 10,
				iat: 5,
				account: "local", // mock possible account ids here
				azp: "test-client",
				email: "test@example.com",
				family_name: "Client",
				given_name: "Test",
				name: "Test Client",
				sub: "prod-rio-users:mock-user",
				locale: config.login.mockLocale,
				tenant: config.login.mockTenant,
			},
			id_token: "id_token",
			session_state: "session_state",
			refresh_token: "refresh_token",
			token_type: "token_type",
			scope: "scope",
			expires_at: 10000000,
			state: "state",
		};

		const user = new User(userSettings);
		oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
		return Promise.resolve(user);
	};

	const clearStaleState = () => {
		console.info("[configuration/login/oidc-session] Stale state cleared");
		return Promise.resolve();
	};

	return { signinSilent, clearStaleState } as UserManager;
};
